/******************************************************************
 * Archivo : client/src/components/User/UserDetailsForm/index.js
 * Proyecto: CB30
 * Módulo  : Usuarios
 * Proceso : Actualización de datos personales
 ******************************************************************/

import React, { Component } from "react";
import { graphql } from "react-apollo";
import _ from "lodash";

// Styles
import "./styles.css";

// Data from JSON
import formFields from "../data/formFields.json";

// Query
import query from "../../../queries/fetchUserById";

// Render Fields
import {
  RenderDateField,
  RenderSelectField,
  RenderNumberField,
  RenderExternalField,
  RenderTextField
} from "./renderFields";

class DetailsFormFields extends Component {
  constructor(props) {
    super(props);

    const filteredKeys = _.filter(formFields, ({ adminOnly }) => !adminOnly);
    const keys = _.map(filteredKeys, ({ name }) => name);
    const emptyString = _.map(formFields, field => "");
    const initialState = _.zipObject(keys, emptyString);

    this.requiredKeys = _.map(
      _.filter(formFields, field => !field.adminOnly && field.required),
      "name"
    );
    this.nonAdminKeys = keys;

    this.state = {
      ...initialState,
      errs: {},
      toast: { show: false, message: "" }
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.showToast = this.showToast.bind(this);
    this.hideToast = this.hideToast.bind(this);
    this._toastTimer = null;
  }

  componentDidMount() {
    if (!this.props.data.loading && this.props.data.userById.validated) {
      // ===== CAMBIO DE LA MIGRACIÓN =====
      const { __typename, ...userDetails } = this.props.data.userById.person;
      // ================================
      return this.setState({ ...userDetails, errs: {} });
    }
    return null;
  }

  componentWillUnmount() {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
  }

  handleOnChange(e) {
    const { name, value, id } = e.target;

    if (id === "mostBeNum") {
      this.handleMustBeNum(value, name);
    }

    this.setState({
      [name]: value
    });
  }

  showToast(message) {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
    }

    this.setState({ toast: { show: true, message } });

    this._toastTimer = setTimeout(() => {
      this.hideToast();
    }, 4000);
  }

  hideToast() {
    this.setState({
      toast: {
        show: false,
        message: ""
      }
    });
  }

  handleEmptyValues() {
    const keysToCheck =
      this.requiredKeys && this.requiredKeys.length
        ? this.requiredKeys
        : this.nonAdminKeys;

    const hasEmptyRequired = _.some(
      keysToCheck,
      key => this.state[key] === ""
    );

    return hasEmptyRequired || !_.isEmpty(this.state.errs);
  }

  handleMustBeNum(value, name) {
    _.isNaN(Number(value))
      ? this.setState(prevState => ({
          errs: {
            ...prevState.errs,
            [name]:
              "Este campo tiene que ser un número entero. No utilice espacios u otros caracteres."
          }
        }))
      : this.setState(prevState => ({
          errs: {
            ..._.omit(prevState.errs, name)
          }
        }));
  }

  btnMode() {
    if (this.props.hideBtn) {
      return "btn btn-secondary disabled";
    }

    return this.handleEmptyValues()
      ? "btn btn-secondary disabled"
      : "btn btn-primary";
  }

  handleSubmit(e) {
    e.preventDefault();

    if (this.handleEmptyValues()) {
      const keysToCheck =
        this.requiredKeys && this.requiredKeys.length
          ? this.requiredKeys
          : this.nonAdminKeys;

      const missingKeys = _.filter(
        keysToCheck,
        key => this.state[key] === ""
      );

      const labelsMap = _.fromPairs(
        _.map(formFields, f => [f.name, f.label])
      );

      const missingLabels = _.map(
        missingKeys,
        key => labelsMap[key]
      ).filter(Boolean);

      const hasErrors = !_.isEmpty(this.state.errs);

      const errsList = _.map(
        this.state.errs,
        (msg, key) => `${labelsMap[key] || key}: ${msg}`
      );

      let alertMsg =
        "Por favor complete los campos obligatorios antes de guardar.";

      if (missingLabels.length) {
        alertMsg += `\n\nFaltan: ${missingLabels.join(", ")}.`;
      }

      if (hasErrors) {
        alertMsg += `\n\nRevisar errores:`;
        alertMsg += `\n- ${errsList.join("\n- ")}`;
      }

      this.showToast(alertMsg);
      return null;
    }

    this.props.sendStateToParent(this.state);
  }

  renderFields() {
    return _.map(formFields, (field, i) => {
      const { type, name, adminOnly } = field;

      if (adminOnly) {
        return null;
      }

      switch (type) {
        case "date":
          return (
            <RenderDateField
              key={i}
              field={field}
              i={i}
              value={this.state[name]}
              handleOnChange={this.handleOnChange}
            />
          );

        case "external":
          return (
            <RenderExternalField
              key={i}
              field={field}
              i={i}
              value={this.state[name]}
              getParentValue={parent => this.state[parent]}
              handleOnChange={this.handleOnChange}
            />
          );

        case "select":
          return (
            <RenderSelectField
              key={i}
              field={field}
              i={i}
              value={this.state[name]}
              handleOnChange={this.handleOnChange}
            />
          );

        case "number":
        case "tel":
          return (
            <RenderNumberField
              key={i}
              field={field}
              i={i}
              value={this.state[name]}
              handleOnChange={this.handleOnChange}
              errs={this.state.errs[name]}
            />
          );

        default:
          return (
            <RenderTextField
              key={i}
              field={field}
              i={i}
              value={this.state[name]}
              handleOnChange={this.handleOnChange}
            />
          );
      }
    });
  }

  render() {
    const { toast } = this.state;

    return (
      <div className="user-details-wrapper">
        <form className="user-details-form">
          <div className="grid-container">{this.renderFields()}</div>

          <button
            onClick={this.handleSubmit}
            className={this.btnMode()}
          >
            Guardar Información
          </button>
        </form>

        {toast && toast.show ? (
          <div
            className="user-details-toast"
            role="alert"
            aria-live="assertive"
          >
            <div className="toast-box toast-error">
              {toast.message}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default graphql(query, {
  options: props => ({
    variables: {
      id: props.userId
    }
  })
})(DetailsFormFields);


// WEBPACK FOOTER //
// ./src/components/User/UserDetailsForm/index.js